import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import repositoryContainer from '../../services/RepositoryContainer';
import { useAuth } from '../../components/context/AuthContext';

const POLL_INTERVAL = 3000;   // 3 s between polls
const MAX_ATTEMPTS  = 20;     // 60 s total before giving up

const SubscriptionCallback = () => {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  // 'polling' | 'activated' | 'timeout'
  const [stage, setStage] = useState('polling');
  const [targetPlan, setTargetPlan] = useState('');
  const attemptsRef = useRef(0);

  useEffect(() => {
    let stored = { previousPlan: null, targetPlan: null };
    try {
      const raw = localStorage.getItem('clawfe:pendingSubscription');
      if (raw) stored = JSON.parse(raw);
    } catch {}

    setTargetPlan(stored.targetPlan || 'new plan');

    const poll = async () => {
      attemptsRef.current += 1;

      try {
        const data = await repositoryContainer.subscription.getCurrent();
        const sub = data?.response ?? data;

        const planActivated = stored.targetPlan
          ? sub?.plan === stored.targetPlan
          : sub?.plan !== stored.previousPlan;

        if (planActivated) {
          localStorage.removeItem('clawfe:pendingSubscription');
          setStage('activated');
          try {
            await refreshUser();
          } catch (refreshError) {
            console.warn('Failed to refresh authenticated user after subscription activation.', refreshError);
          }
          setTimeout(() => {
            navigate('/settings', { state: { subscriptionSuccess: true }, replace: true });
          }, 2000);
          return;
        }
      } catch {}

      if (attemptsRef.current >= MAX_ATTEMPTS) {
        setStage('timeout');
        return;
      }

      setTimeout(poll, POLL_INTERVAL);
    };

    // Small initial delay so the webhook has time to fire
    const initial = setTimeout(poll, 2000);
    return () => clearTimeout(initial);
  }, [navigate]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="text-center space-y-6 max-w-sm w-full">

        {stage === 'polling' && (
          <>
            <div className="relative mx-auto w-20 h-20">
              <div className="absolute inset-0 rounded-full border-4 border-primary/20" />
              <div className="w-20 h-20 rounded-full border-4 border-primary border-t-transparent animate-spin" />
            </div>
            <div className="space-y-2">
              <h1 className="font-heading font-bold text-2xl text-foreground">Activating your plan…</h1>
              <p className="text-muted-foreground font-caption text-sm">
                Payment received. Waiting for your <span className="font-semibold text-foreground">{targetPlan}</span> plan to activate.
              </p>
            </div>
            <p className="text-xs text-muted-foreground">This usually takes a few seconds.</p>
          </>
        )}

        {stage === 'activated' && (
          <>
            <div className="relative mx-auto w-20 h-20">
              <div className="absolute inset-0 rounded-full bg-green-100 animate-ping opacity-40" />
              <div className="relative w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
                <svg className="w-9 h-9 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <div className="space-y-2">
              <h1 className="font-heading font-bold text-2xl text-foreground">You're on {targetPlan}!</h1>
              <p className="text-muted-foreground font-caption text-sm">
                Taking you back to settings…
              </p>
            </div>
          </>
        )}

        {stage === 'timeout' && (
          <>
            <div className="mx-auto w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center">
              <svg className="w-9 h-9 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="space-y-2">
              <h1 className="font-heading font-bold text-2xl text-foreground">Taking longer than usual</h1>
              <p className="text-muted-foreground font-caption text-sm">
                Your payment was received but the plan hasn't reflected yet. Check your settings in a moment — it should update shortly.
              </p>
            </div>
            <button
              onClick={() => navigate('/settings', { replace: true })}
              className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:opacity-90 transition-opacity"
            >
              Go to Settings
            </button>
          </>
        )}

      </div>
    </div>
  );
};

export default SubscriptionCallback;
